/**
 * COP Pathfinder Chat Service
 * Handles REST API calls and SSE streaming for user-to-user chat.
 */

import { withSseAccessToken } from '@signals/app-shell/utils/sseAuthUrl';

export interface ChatReaction {
  emoji: string;
  user_email: string;
  user_name: string;
}

export interface ChatMessage {
  id: number;
  channel: string;
  user_email: string;
  user_name: string;
  message: string;
  message_type: 'text' | 'alert' | 'system' | 'image';
  is_edited: boolean;
  created_at: string;
  updated_at?: string | null;
  attachment_url?: string | null;
  attachment_mime_type?: string | null;
  attachment_name?: string | null;
  reactions?: ChatReaction[];
}

export interface ChatHistoryResponse {
  messages: ChatMessage[];
  count: number;
  has_more: boolean;
}

export interface ChatChannel {
  channel: string;
  message_count: number;
  last_message_at: string | null;
}

export interface ChatUser {
  email: string;
  name: string;
  last_active: string | null;
}

class COPTeamChatService {
  private baseUrl = '/api/cop-team-chat';

  private get authHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
    };
  }

  private get authOnlyHeaders(): Record<string, string> {
    return {
      Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
    };
  }

  async getMessages(
    channel = 'general',
    limit = 50,
    beforeId?: number
  ): Promise<ChatHistoryResponse> {
    const params = new URLSearchParams({ channel, limit: String(limit) });
    if (beforeId) params.append('before_id', String(beforeId));

    const res = await fetch(`${this.baseUrl}/messages?${params}`, {
      headers: this.authHeaders,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async sendMessage(
    message: string,
    channel = 'general',
    messageType: 'text' | 'alert' = 'text'
  ): Promise<ChatMessage> {
    const res = await fetch(`${this.baseUrl}/messages`, {
      method: 'POST',
      headers: this.authHeaders,
      body: JSON.stringify({ message, channel, message_type: messageType }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return res.json();
  }

  async sendImageMessage(file: File, channel = 'general', caption = ''): Promise<ChatMessage> {
    const form = new FormData();
    form.append('channel', channel);
    form.append('caption', caption);
    form.append('file', file);

    const res = await fetch(`${this.baseUrl}/messages/image`, {
      method: 'POST',
      headers: this.authOnlyHeaders,
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return res.json();
  }

  async getMediaObjectUrl(attachmentUrl: string): Promise<string> {
    const res = await fetch(attachmentUrl, {
      headers: this.authOnlyHeaders,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }

  async editMessage(messageId: number, message: string, channel = 'general'): Promise<ChatMessage> {
    const res = await fetch(`${this.baseUrl}/messages/${messageId}`, {
      method: 'PUT',
      headers: this.authHeaders,
      body: JSON.stringify({ message, channel }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return res.json();
  }

  async deleteMessage(messageId: number): Promise<void> {
    const res = await fetch(`${this.baseUrl}/messages/${messageId}`, {
      method: 'DELETE',
      headers: this.authHeaders,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
  }

  async getChannels(): Promise<ChatChannel[]> {
    const res = await fetch(`${this.baseUrl}/channels`, {
      headers: this.authHeaders,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.channels;
  }

  async createChannel(channelName: string): Promise<{ channel: string; created: boolean }> {
    const res = await fetch(
      `${this.baseUrl}/channels?channel_name=${encodeURIComponent(channelName)}`,
      { method: 'POST', headers: this.authHeaders }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return res.json();
  }

  async getUsers(): Promise<ChatUser[]> {
    const res = await fetch(`${this.baseUrl}/users`, {
      headers: this.authHeaders,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.users;
  }

  async sendTyping(channel = 'general'): Promise<void> {
    await fetch(`${this.baseUrl}/typing?channel=${encodeURIComponent(channel)}`, {
      method: 'POST',
      headers: this.authHeaders,
    }).catch(() => {});
  }

  async addReaction(messageId: number, emoji: string): Promise<void> {
    const res = await fetch(
      `${this.baseUrl}/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`,
      { method: 'POST', headers: this.authHeaders }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  }

  async removeReaction(messageId: number, emoji: string): Promise<void> {
    const res = await fetch(
      `${this.baseUrl}/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`,
      { method: 'DELETE', headers: this.authHeaders }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  }

  /**
   * Open an SSE connection for real-time chat events.
   * Returns the EventSource so the caller can close it.
   */
  connectStream(
    channel: string | null,
    onMessage: (msg: ChatMessage) => void,
    onEdited?: (msg: ChatMessage) => void,
    onDeleted?: (data: { id: number; channel: string }) => void,
    onError?: (err: Event) => void,
    onTyping?: (data: { user_email: string; user_name: string; channel: string }) => void,
    onReactionAdded?: (data: { message_id: number; emoji: string; user_email: string; user_name: string; channel: string }) => void,
    onReactionRemoved?: (data: { message_id: number; emoji: string; user_email: string; channel: string }) => void,
  ): EventSource {
    const params = channel ? `?channel=${encodeURIComponent(channel)}` : '';
    const es = new EventSource(withSseAccessToken(`${this.baseUrl}/stream${params}`));

    es.addEventListener('new_message', (e) => {
      try { onMessage(JSON.parse(e.data)); } catch { /* ignore */ }
    });

    if (onEdited) {
      es.addEventListener('message_edited', (e) => {
        try { onEdited(JSON.parse(e.data)); } catch { /* ignore */ }
      });
    }

    if (onDeleted) {
      es.addEventListener('message_deleted', (e) => {
        try { onDeleted(JSON.parse(e.data)); } catch { /* ignore */ }
      });
    }

    if (onTyping) {
      es.addEventListener('user_typing', (e) => {
        try { onTyping(JSON.parse(e.data)); } catch { /* ignore */ }
      });
    }

    if (onReactionAdded) {
      es.addEventListener('reaction_added', (e) => {
        try { onReactionAdded(JSON.parse(e.data)); } catch { /* ignore */ }
      });
    }

    if (onReactionRemoved) {
      es.addEventListener('reaction_removed', (e) => {
        try { onReactionRemoved(JSON.parse(e.data)); } catch { /* ignore */ }
      });
    }

    es.onerror = (e) => {
      onError?.(e);
    };

    return es;
  }
}

export const copTeamChatService = new COPTeamChatService();
